import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * `<rotary-encoder>` — continuous or detented rotary primitive.
 *
 * Models the dominant control class on every shading panel and the iris
 * binding on supervisory panels (MCP-400, MSU-3000). The visual composes
 * a skirted cap, an engraved pointer line and an optional illumination
 * ring at the base — broadcast convention for an addressable encoder.
 *
 * Colour, finish and the illumination hue are all tokenised. The
 * primitive owns no vendor logic; theming flows through CSS custom
 * properties bound at panel composition time.
 */
@customElement('rotary-encoder')
export class RotaryEncoderElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: inline-block;
        contain: layout paint;
        aspect-ratio: 1;
        inline-size: 100%;

        --re-cap: var(--control-cap, #2a2a2c);
        --re-cap-highlight: var(--control-cap-highlight, #4a4a4d);
        --re-skirt: var(--control-skirt, #18181a);
        --re-pointer: var(--control-pointer, #d8d8da);
        --re-led: var(--encoder-led-colour, #16a34a);
      }

      svg {
        display: block;
        inline-size: 100%;
        block-size: 100%;
        overflow: visible;
      }

      .rotary-stack {
        position: relative;
        inline-size: 100%;
        block-size: 100%;
      }

      /* Vertical channel-indicator line from the cap edge to the cap
       * centre, pointing UP at the default (12 o'clock) rotation. This
       * is the real OCP-400 rubber-cap marker: a half-radius slit, not
       * a full diameter stripe. Rendered as an HTML overlay above the
       * SVG to bypass the Chromium shadow-DOM rect bug. */
      .pointer-overlay {
        position: absolute;
        left: 48%;
        top: 22%;
        inline-size: 4%;
        block-size: 28%;
        background: var(--re-pointer);
        border-radius: 40%;
        pointer-events: none;
        transform-origin: 50% 100%;
        transform: rotate(var(--re-rotation, 0deg));
      }

      .cap {
        transform-origin: 50px 50px;
        transition: transform 80ms ease-out;
      }

      .rotary-stack {
        cursor: ns-resize;
        touch-action: none;
        user-select: none;
      }

      .rotary-stack:active {
        cursor: grabbing;
      }

      .pointer-stripe {
        fill: var(--re-pointer);
      }

      /* LED-ring strokes flow through CSS rather than the SVG
       * stroke= presentation attribute. WebKit and Blink both
       * treat var() inside an SVG presentation attribute as an
       * unparseable value and fall back to the SVG default
       * stroke:none, so a stroke="var(--re-led)" attribute renders
       * no stroke. The CSS stroke property resolves var() correctly. */
      .led-ring-outer {
        fill: none;
        stroke: var(--re-led);
        stroke-width: 5;
        opacity: 0.85;
        filter: url(#re-glow);
      }

      .led-ring-inner {
        fill: none;
        stroke: var(--re-led);
        stroke-width: 2.2;
        opacity: 1;
      }
    `,
  ];

  /** Number of detents per revolution. Zero means smooth/continuous. */
  @property({ type: Number }) accessor detents = 24;

  /** Whether the cap can be pressed (axial push). */
  @property({ type: Boolean, reflect: true }) accessor pressable = false;

  /**
   * Whether the encoder ships with an addressable LED halo.
   *
   * The attribute name is explicit kebab-case so panel compositions
   * can write `?led-ring=${true}` in lit-html templates and reach the
   * property via Lit's attribute reflection. Without this, Lit
   * defaults the attribute to the lowercase property name
   * (`ledring`), which silently drops the binding.
   */
  @property({ type: Boolean, reflect: true, attribute: 'led-ring' }) accessor ledRing = false;

  /** Lit state of the LED ring. Off when ledRing is false. */
  @property({ type: Boolean, reflect: true }) accessor illuminated = false;

  /** Whether the cap carries an engraved indicator pointer. */
  @property({ type: Boolean }) accessor hasPointer = true;

  /** Current cap rotation, degrees. Animated by interaction handlers. */
  @property({ type: Number }) accessor rotation = 0;

  /**
   * Degrees of rotation per pixel of vertical drag. Drag UP rotates
   * clockwise (positive) — broadcast convention for "increase the
   * parameter".
   *
   * PROVISIONAL DEFAULT: 1°/px. NOT derived from the EM14 encoder's
   * pulse-per-degree spec or from operator feel data; chosen so a
   * comfortable thumb drag spans about a full revolution. Override
   * via property assignment from the panel until a hardware-grounded
   * value is available.
   */
  @property({ type: Number }) accessor dragSensitivity = 1;

  /**
   * Degrees of rotation per wheel notch (mouse-wheel input).
   *
   * PROVISIONAL DEFAULT: 15°. Not derived from any hardware spec or
   * convention — picked so a single notch is visually clear without
   * over-spinning the cap. Replace with the encoder's documented
   * detent angle when that lands.
   */
  @property({ type: Number }) accessor wheelStep = 15;

  #dragPointerId = -1;
  #dragStartClientY = 0;
  #dragStartRotation = 0;

  #onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    this.#dragPointerId = event.pointerId;
    this.#dragStartClientY = event.clientY;
    this.#dragStartRotation = this.rotation;
    (event.currentTarget as Element).setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  #onPointerMove = (event: PointerEvent) => {
    if (event.pointerId !== this.#dragPointerId) return;
    const dy = event.clientY - this.#dragStartClientY;
    // Drag UP (negative dy) → positive rotation (clockwise).
    const next = this.#dragStartRotation - dy * this.dragSensitivity;
    this.#applyRotation(next);
  };

  #onPointerUp = (event: PointerEvent) => {
    if (event.pointerId !== this.#dragPointerId) return;
    this.#dragPointerId = -1;
    const target = event.currentTarget as Element;
    if (target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }
  };

  #onWheel = (event: WheelEvent) => {
    event.preventDefault();
    const dir = event.deltaY > 0 ? -1 : 1;
    this.#applyRotation(this.rotation + dir * this.wheelStep);
  };

  #applyRotation(target: number) {
    const next = this.detents > 0
      ? Math.round(target / (360 / this.detents)) * (360 / this.detents)
      : target;
    if (next === this.rotation) return;
    this.rotation = next;
    this.dispatchEvent(new CustomEvent('rotation-change', {
      bubbles: true,
      composed: true,
      detail: { rotation: next },
    }));
  }

  override render() {
    const showRing = this.ledRing && this.illuminated;
    return html`
      <div
        class="rotary-stack"
        @pointerdown=${this.#onPointerDown}
        @pointermove=${this.#onPointerMove}
        @pointerup=${this.#onPointerUp}
        @pointercancel=${this.#onPointerUp}
        @wheel=${this.#onWheel}
      >
      <svg viewBox="0 0 100 100" role="slider" aria-label="rotary encoder">
        <defs>
          <radialGradient id="re-cap" cx="0.5" cy="0.4" r="0.55">
            <stop offset="0" stop-color="var(--re-cap-highlight)" />
            <stop offset="0.55" stop-color="var(--re-cap)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--re-cap) 55%, black)" />
          </radialGradient>
          <radialGradient id="re-skirt" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0.8" stop-color="var(--re-skirt)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--re-skirt) 35%, black)" />
          </radialGradient>
          <filter id="re-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="2.2" />
          </filter>
        </defs>

        <!-- Chassis well behind the encoder, creates the recessed-into-metal feel. -->
        <circle cx="50" cy="50" r="46" fill="color-mix(in oklab, var(--re-skirt) 40%, black)" />

        ${showRing
          ? html`
              <circle class="led-ring-outer" cx="50" cy="50" r="42" />
              <circle class="led-ring-inner" cx="50" cy="50" r="40" />
            `
          : ''}

        <!-- Skirt -->
        <circle cx="50" cy="50" r="36" fill="url(#re-skirt)" />

        <!-- Cap (rotates with value). -->
        <g class="cap" style="transform: rotate(${this.rotation}deg);">
          <circle cx="50" cy="50" r="30" fill="url(#re-cap)" />
        </g>

        <!-- Pointer stripe. Drawn OUTSIDE the transform group so the
             SVG rect renders without the transform-box quirk that
             collapses its bounding box to 0×0 in Chromium when nested
             in a rotated <g>. The stripe still indicates the dial's
             channel assignment (red/green/blue on the RGB encoder rows
             of the OCP-400). Colour flows in through --re-pointer. -->
      </svg>
      ${this.hasPointer
        ? html`<div
            class="pointer-overlay"
            style="transform: rotate(${this.rotation}deg);"
          ></div>`
        : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rotary-encoder': RotaryEncoderElement;
  }
}
